<template>
  <div :class="[$options.name, statusClassList, 'tw:text-sm tw:leading-5']">
    <label v-if="props.label" :class="['tw:body-1', `${$options.name}__label`]" :for="id">{{ props.label }}</label>
    <textarea
      :id="id"
      ref="textareaRef"
      :class="[
        `${$options.name}__area`,
        'tw:border tw:border-default-200 tw:text-black tw:placeholder:text-default-600 tw:hover:border-default-400 tw:focus:border-blue-500 tw:focus:outline-hidden',
        {
          'tw:border-red-500': (invalid || internalInvalid) && !disabled,
          'tw:disabled:bg-default-100 tw:disabled:border-default-100 tw:text-default-800 tw:placeholder:text-default-500':
            disabled && !(invalid || internalInvalid),
          'tw:disabled:bg-red-000 tw:disabled:text-red-500 tw:disabled:border-red-000':
            disabled && (invalid || internalInvalid),
          [`${$options.name}__area--auto-resize`]: autoResize,
        },
      ]"
      :disabled="disabled"
      :maxlength="props.maxlength"
      :placeholder="props.placeholder"
      :value="internalValue"
      :rows="rows"
      @click="gtagUniversalSend({ component_id: uniqueId })"
      @change="handleChange"
      @input="handleInput($event)"
      @paste="$emit('paste', $event)"
      @blur="$emit('blur', $event)"
      @keydown.enter.exact="$emit('enter', internalValue)"
    ></textarea>
    <div v-if="showErrorMsg || showHelper || getMaxlength" class="tw:flex tw:justify-between">
      <p v-if="showErrorMsg" class="tw:caption tw:!text-red-500">
        {{ props.errorMsg }}
      </p>
      <p v-else-if="showHelper" class="tw:caption tw:!text-default-700">{{ props.helper }}</p>
      <slot>
        <p v-if="getMaxlength" class="tw:caption tw:!text-default-700 tw:ms-auto tw:my-0">
          {{ internalValue.length }}/{{ getMaxlength }}
        </p>
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, useTemplateRef, onMounted } from "vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneTextarea",
  props: {
    /**
     * Value of the textarea
     */
    value: {
      type: String,
      default: "",
    },
    /**
     * auto focus input after rendering it
     */
    focused: {
      type: Boolean,
      default: false,
    },
    /**
     * Number of rows in textarea
     */
    rows: {
      type: Number,
      default: 5,
    },
    /**
     * Value of text in textarea until user types something
     */
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * Whether the type textarea is disabled or not (greyed out and not clickable)
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the type textarea is invalid (red border and red helper text)
     */
    invalid: {
      type: Boolean,
      default: false,
    },
    /**
     * Value of text at the top of the textarea
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Value of text below the textarea if invalid
     */
    errorMsg: {
      type: String,
      default: "",
    },
    /**
     * Value of text below the textarea
     */
    helper: {
      type: String,
      default: "",
    },
    /**
     * maxlength of letters in textarea (number of letters entered is shown below the textarea)
     */
    maxlength: {
      type: Number,
      default: null,
    },
    uniqueId: {
      type: String,
      default: "",
    },
    autoResize: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Triggers at every value change (single char input)
     */
    "input",
    /**
     * Triggers when you click Enter on keyboard
     */
    "enter",
    /**
     * Triggers when you click outside the input
     */
    "change",
    /**
     * Triggers when you paste content
     */
    "paste",
    "blur",
  ],
  setup(props, { emit }) {
    const internalValue = ref(props.value);
    const internalInvalid = ref(props.invalid);
    const id = new Date().getTime().toString();
    const textareaRef = useTemplateRef<HTMLTextAreaElement>("textareaRef");

    onMounted(() => {
      if (props.focused && textareaRef.value) {
        textareaRef.value.focus();
      }
    });

    watch(
      () => props.value,
      () => {
        internalValue.value = props.value;
      }
    );

    const getMaxlength = computed(() => {
      if (props.maxlength < 0) return null;
      return props.maxlength;
    });

    function handleInput(event: Event) {
      if (internalValue.value.length < props.maxlength) {
        internalInvalid.value = false;
      }
      internalValue.value = (event.target as HTMLInputElement).value;
      emit("input", internalValue.value);
    }

    function handleChange() {
      internalInvalid.value = false;
      emit("change", internalValue.value);
    }

    const statusClassList = computed(() => {
      return {
        "EdroneTextarea--default": !props.disabled && !props.invalid,
        "EdroneTextarea--invalid": internalInvalid.value || (props.invalid && !props.disabled),
        "EdroneTextarea--disabled": props.disabled || (props.disabled && props.invalid),
      };
    });

    const showHelper = computed(() => {
      return (props.helper && !props.invalid) || (props.disabled && props.invalid);
    });

    const showErrorMsg = computed(() => {
      return props.errorMsg && props.invalid && !props.disabled;
    });

    const focus = () => {
      textareaRef.value?.focus();
    };

    return {
      props,
      internalValue,
      internalInvalid,
      id,
      handleInput,
      handleChange,
      getMaxlength,
      statusClassList,
      showHelper,
      showErrorMsg,
      focus,
    };
  },
  methods: { gtagUniversalSend },
});
</script>

<style lang="scss" scoped>
.EdroneTextarea {
  display: inline-flex;
  flex-direction: column;
  width: 100%;
  gap: 0.25rem;

  textarea {
    display: block;
    resize: none;
    width: 100%;
    outline: none;
    font-weight: inherit;
    font-size: inherit;
    line-height: inherit;
    font-family: "Roboto", sans-serif;
    padding: 0.625rem 0.75rem;
    border-radius: 0.25rem;
  }

  &__area--auto-resize {
    field-sizing: content;
    max-height: 8rem;
  }

  &__label {
    text-overflow: ellipsis;
    max-width: 100%;
    overflow: hidden;
    margin: 0;
  }
}
</style>
